import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const API_BASE_URL =
  (process.env.REACT_APP_API_URL || "https://tools.microters.com").replace(/\/$/, "");

const COLORS = {
  background: "#fff9f7",
  navy: "#1e2b49",
  orange: "#ff5a36",
  muted: "#68758c",
  border: "#dce3ec",
};

const TONES = [
  "Formal",
  "Professional",
  "Academic",
  "Friendly",
  "Conversational",
  "Persuasive",
  "Creative",
  "Confident",
];

const LANGUAGES = [
  "English",
  "Bengali",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Hindi",
  "Arabic",
  "Chinese",
  "Japanese",
  "Korean",
];

const countWords = (value) => value.trim().match(/[^\s]+/g)?.length || 0;

const getErrorMessage = (error) =>
  error.response?.data?.message ||
  error.response?.data?.errors?.[0] ||
  (error.code === "ECONNABORTED"
    ? "The request timed out. Please try again."
    : error.message || "Could not expand the sentence.");

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
};

export default function AISentenceExpander() {
  const [sentence, setSentence] = useState("");
  const [tone, setTone] = useState("Formal");
  const [language, setLanguage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState(null);

  const inputWordCount = useMemo(() => countWords(sentence), [sentence]);

  const clearMessages = () => {
    if (error) setError("");
    if (notice) setNotice("");
  };

  const handleExpand = async (event) => {
    event.preventDefault();
    const cleanSentence = sentence.trim();

    if (!cleanSentence) {
      setError("Please enter the original sentence or text.");
      return;
    }

    if (!tone) {
      setError("Please select a tone.");
      return;
    }

    if (!language) {
      setError("Please select a language.");
      return;
    }

    setLoading(true);
    setError("");
    setNotice("");
    setResult(null);

    console.log("[SENTENCE EXPANDER UI] Sending request:", {
      endpoint: `${API_BASE_URL}/api/sentence-expander`,
      inputCharacters: cleanSentence.length,
      inputWords: countWords(cleanSentence),
      tone,
      language,
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/sentence-expander`,
        { sentence: cleanSentence, tone, language },
        { timeout: 70000 }
      );

      const responseData = response.data?.data;

      if (!response.data?.success || !responseData?.expandedSentence?.trim()) {
        throw new Error("The server returned an empty expansion.");
      }

      setResult({
        ...responseData,
        alternatives: Array.isArray(responseData.alternatives)
          ? responseData.alternatives.filter(Boolean)
          : [],
      });
      setNotice("Sentence expanded successfully.");

      console.log("[SENTENCE EXPANDER UI] Result received:", {
        originalWords: responseData.originalWordCount,
        expandedWords: responseData.expandedWordCount,
        expansionRatio: responseData.expansionRatio,
      });
    } catch (requestError) {
      const message = getErrorMessage(requestError);
      setError(message);

      console.error("[SENTENCE EXPANDER UI] Request failed:", {
        message,
        status: requestError.response?.status,
        response: requestError.response?.data,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, label = "Expanded text") => {
    try {
      await copyText(text);
      setError("");
      setNotice(`${label} copied to the clipboard.`);
    } catch (copyError) {
      console.error("[SENTENCE EXPANDER UI] Copy failed:", copyError);
      setError("Could not copy the expanded text.");
    }
  };

  const handleCopyAll = async () => {
    if (!result?.expandedSentence) return;

    const alternativeText = result.alternatives
      .map((item, index) => `Alternative ${index + 1}:\n${item}`)
      .join("\n\n");
    const text = `Expanded Text:\n${result.expandedSentence}${
      alternativeText ? `\n\n${alternativeText}` : ""
    }`;

    await handleCopy(text, "All expansions");
  };

  const handleDownload = () => {
    if (!result?.expandedSentence) return;

    const alternativeText = result.alternatives
      .map((item, index) => `Alternative ${index + 1}:\n${item}`)
      .join("\n\n");
    const text = `Original Text:\n${sentence.trim()}\n\nExpanded Text:\n${
      result.expandedSentence
    }${alternativeText ? `\n\n${alternativeText}` : ""}`;
    const file = new Blob([text], { type: "text/plain;charset=utf-8" });
    const fileUrl = URL.createObjectURL(file);
    const anchor = document.createElement("a");
    anchor.href = fileUrl;
    anchor.download = "ai-expanded-sentence.txt";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(fileUrl);
    setError("");
    setNotice("Expanded text downloaded as a TXT file.");
  };

  const handleReset = () => {
    setSentence("");
    setTone("Formal");
    setLanguage("");
    setLoading(false);
    setError("");
    setNotice("");
    setResult(null);
  };

  return (
    <main
      className="min-vh-100 py-5"
      style={{ backgroundColor: COLORS.background, color: COLORS.navy }}
    >
      <Container style={{ maxWidth: "1120px" }}>
      

        <Card className="border rounded-4 shadow-sm" style={{ borderColor: COLORS.border }}>
          <Card.Body className="p-4 p-md-5">
            <Form onSubmit={handleExpand} noValidate>
              <Form.Group className="mb-4" controlId="originalSentence">
                <Form.Label className="fw-semibold">
                  Original Sentence <span style={{ color: COLORS.orange }}>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  maxLength={500}
                  value={sentence}
                  disabled={loading}
                  placeholder="Enter the sentence or short text you want to expand..."
                  onChange={(event) => {
                    setSentence(event.target.value);
                    clearMessages();
                  }}
                  style={{ minHeight: "130px", resize: "vertical" }}
                />
                <div
                  className="d-flex justify-content-between small mt-2"
                  style={{ color: COLORS.muted }}
                >
                  <span>{inputWordCount.toLocaleString()} words</span>
                  <span>{sentence.length.toLocaleString()}/500 characters</span>
                </div>
              </Form.Group>

              <Row className="g-3 mb-4">
                <Col md={6}>
                  <Form.Group controlId="expanderTone">
                    <Form.Label className="fw-semibold">
                      Tone <span style={{ color: COLORS.orange }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={tone}
                      disabled={loading}
                      onChange={(event) => {
                        setTone(event.target.value);
                        clearMessages();
                      }}
                    >
                      <option value="">Select tone...</option>
                      {TONES.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="expanderLanguage">
                    <Form.Label className="fw-semibold">
                      Language <span style={{ color: COLORS.orange }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={language}
                      disabled={loading}
                      onChange={(event) => {
                        setLanguage(event.target.value);
                        clearMessages();
                      }}
                    >
                      <option value="">Select language...</option>
                      {LANGUAGES.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {error && (
                <Alert variant="danger" className="py-2" role="alert">
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-100 border-0 rounded-pill fw-bold py-3 shadow-sm"
                style={{ backgroundColor: COLORS.orange }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Expanding Sentence...
                  </>
                ) : (
                  <>✦&nbsp; Expand Sentence</>
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {result && (
          <Card className="border-0 rounded-4 shadow-sm mt-4 overflow-hidden">
            <Card.Header className="bg-white border-bottom p-4">
              <div className="d-flex flex-column flex-lg-row gap-3 justify-content-between align-items-lg-center">
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-success fs-5" aria-hidden="true">✓</span>
                    <h2 className="h4 fw-bold mb-0">Expanded Sentence</h2>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {(result.originalWordCount ?? inputWordCount).toLocaleString()} original words
                    </Badge>
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {(result.expandedWordCount ?? countWords(result.expandedSentence)).toLocaleString()} expanded words
                    </Badge>
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {Number(result.expansionRatio || 0).toFixed(2)}× expansion
                    </Badge>
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {result.language || language}
                    </Badge>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button variant="outline-secondary" onClick={handleCopyAll}>
                    Copy All
                  </Button>
                  <Button variant="outline-secondary" onClick={handleDownload}>
                    Download TXT
                  </Button>
                  <Button variant="light" className="border" onClick={handleReset}>
                    Start Over
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-4 p-md-5">
              {notice && (
                <Alert variant="success" className="py-2" role="status">
                  {notice}
                </Alert>
              )}

              <div className="p-4 rounded-3 bg-light border">
                <div className="d-flex gap-3 justify-content-between align-items-start">
                  <p className="fs-5 mb-0" style={{ color: "#34425b", lineHeight: 1.75 }}>
                    {result.expandedSentence}
                  </p>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleCopy(result.expandedSentence)}
                  >
                    Copy
                  </Button>
                </div>
              </div>

              {result.alternatives.length > 0 && (
                <div className="mt-4">
                  <h3 className="h5 fw-bold mb-3">Alternative Expansions</h3>
                  <div className="d-grid gap-3">
                    {result.alternatives.map((item, index) => (
                      <div key={`${item.slice(0, 30)}-${index}`} className="p-3 rounded-3 border">
                        <div className="d-flex gap-3 justify-content-between align-items-start">
                          <p className="mb-0" style={{ color: "#48556c", lineHeight: 1.65 }}>
                            {item}
                          </p>
                          <Button
                            variant="light"
                            className="border"
                            size="sm"
                            onClick={() => handleCopy(item, `Alternative ${index + 1}`)}
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </main>
  );
}
